import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { MessageSquare } from 'lucide-react';

/**
 * Web-only "heads up" notice that opens BEFORE the multi-recipient
 * `sms:` link is fired. Browsers handle multi-recipient SMS
 * inconsistently (Safari often drops the 2nd number); this dialog
 * warns the user and surfaces the recipient phones for easy copy-paste.
 *
 * Pure presentational. The parent decides when to show / hide and what
 * to do on Continue (see `confirmAndSendSms` in MakeIntrosButton).
 */
export default function IntrosSmsWebConfirm({
  open,
  onOpenChange,
  os,
  pickedContacts,
  onCopyPhone,
  onCancel,
  onContinue,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="make-intros-sms-confirm">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageSquare size={16} className="text-amber-600" />
            Heads up — web limitation
          </DialogTitle>
          <DialogDescription className="text-xs space-y-2 pt-1">
            <span className="block">
              You&apos;re using the PeopleiKnow web app. Browsers handle
              multi-recipient SMS differently — {os === 'ios'
                ? 'on iPhone in particular, Safari often opens the thread with only the '
                : 'your browser may open the thread with only the '}
              <strong>first</strong> contact.
            </span>
            <span className="block">
              When your Messages app opens, please double-check that both
              recipients are included; if not, just type or paste the
              second number into the To: field.
            </span>
            <span className="block">
              (When PeopleiKnow ships as a native app, both contacts will
              be added automatically.)
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="flex-1 rounded-full"
            data-testid="sms-confirm-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onContinue}
            className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="sms-confirm-continue-btn"
          >
            Open Messages
          </Button>
        </div>
        {/* Recipient numbers visible right on this notice so users can
            copy/paste them quickly if their browser drops one. */}
        <div className="mt-3 pt-3 border-t border-border space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Recipients</p>
          {pickedContacts.map((c) => {
            const phone = (c.phones || [])[0]?.number;
            return (
              <div key={c.id} className="flex items-center justify-between text-xs">
                <span className="font-medium">{c.first_name} {c.last_name}</span>
                <button
                  type="button"
                  onClick={() => onCopyPhone(phone || '', c.id)}
                  className="font-mono text-[11px] text-nav-blue hover:underline"
                  data-testid={`sms-recipient-phone-${c.id}`}
                >
                  {phone || '— no phone —'}
                </button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
